import { Panel } from '$tablet-app/src/app/components/Panel';
import Spacer from '$tablet-app/src/app/components/Spacer';
import { Button } from '@akin-home/buttons/native';
import { Goal } from '@akin-home/goals';
import { Text } from '@akin-home/text/native';
import React, { FC } from 'react';
import { Pressable, StyleProp, View, ViewStyle } from 'react-native';
import styled from 'styled-components/native';
import {
  ProgressBar,
  ProgressBarCompletedMessage,
  ProgressBarCountdown,
} from '../ProgressBar';

interface Props {
  /** The goal being rendered */
  goal: Goal;

  progress?: number;

  /** Callback when the goal is marked as completed */
  onPress?: (goal: Goal) => void;
  onLongPress?: (goal: Goal) => void;
  onPressEdit?: (goal: Goal) => void;

  style?: StyleProp<ViewStyle>;

  // Should we show the small width variant
  smallVariant?: boolean;
}

const GoalCard: FC<Props> = (props) => {
  const {
    goal,
    progress = 0,
    style,
    onPress,
    onLongPress,
    onPressEdit,
    smallVariant = false,
  } = props;

  const { title } = goal;

  const complete = () => onPress?.(goal);
  const decrement = () => onLongPress?.(goal);

  const handleEditPress = () => onPressEdit?.(goal);

  const remaining = goal.completions_per_period - progress;

  const finished = remaining === 0;

  return (
    <Card style={style}>
      <TitleRow
        isSmallVariant={smallVariant}
        style={({ pressed }) => ({
          opacity: pressed ? 0.5 : 1,
        })}
        onPress={handleEditPress}
        hitSlop={10}
      >
        <EmojiContainer>
          <Emoji>{goal.emoji}</Emoji>
        </EmojiContainer>
        <Title
          variant="h3"
          weight="semibold"
          numberOfLines={2}
          isSmallVariant={smallVariant}
        >
          {title}
        </Title>
      </TitleRow>

      <ProgressSection
        style={({ pressed }) => ({
          opacity: pressed ? 0.5 : 1,
        })}
        onPress={complete}
        onLongPress={decrement}
        hitSlop={10}
      >
        <ProgressBar target={goal.completions_per_period} current={progress} />

        {finished ? (
          <ProgressBarCompletedMessage />
        ) : (
          <ProgressBarCountdown remaining={remaining} />
        )}
      </ProgressSection>

      <Spacer height="1px" color="#DDDDDD" />

      <ButtonRow isSmallVariant={smallVariant}>
        <Button
          variant="text"
          icon="pencil"
          size="small"
          onPress={handleEditPress}
        >
          Change
        </Button>

        {progress > 0 ? (
          <Button variant="text" icon="undo" size="small" onPress={decrement}>
            Undo
          </Button>
        ) : null}

        {!finished ? (
          <Button
            variant="secondary"
            icon="check"
            size="small"
            onPress={complete}
          >
            Done
          </Button>
        ) : null}
      </ButtonRow>
    </Card>
  );
};

const Card = styled(Panel)`
  padding: 20px;
  gap: 20px;
  position: relative;
`;

const TitleRow = styled(Pressable)<{ isSmallVariant?: boolean }>`
  ${({ isSmallVariant }) =>
    isSmallVariant ? `flex-direction: column;` : `flex-direction: row;`}
  flex-grow: 1;
  gap: 15px;
  align-items: center;
`;

const Title = styled(Text)<{ isSmallVariant?: boolean }>`
  overflow: hidden;
  ${({ isSmallVariant }) => (isSmallVariant ? `width: 100%;` : `flex: 1`)}
`;

const EmojiContainer = styled.View`
  height: 50px;
  width: 50px;
  background-color: rgba(83, 90, 241, 0.1);
  align-items: center;
  justify-content: center;
  border-radius: 25px;
`;

const Emoji = styled.Text`
  font-size: 25px;
`;

const ButtonRow = styled(View)<{ isSmallVariant?: boolean }>`
  flex-direction: row;
  justify-content: space-evenly;
  gap: 10px;
  ${({ isSmallVariant }) => (isSmallVariant ? 'flex-wrap: wrap;' : '')}
`;

const ProgressSection = styled(Pressable)`
  gap: 10px;
`;

export default GoalCard;
